import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { Box, Grid } from "@mui/material";
import { FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import CustomSelectScroll from "../../../../../components/CustomSelectScroll";
import Input from "../../../../../components/Input";
import { AddSuperBillForm } from "../../../../../constants/provider/super-bill/add-super-bill/add-super-bill";
import { useProviderListV2 } from "../../../../../queries/use-provider-list-v2";
import { ProviderListItem } from "../../../../../types/provider-types";
import CardView from "../../patient/patient-list/patient-chart-new/assessment/summary/components/CardView";
import { DEF_PAGINATION, DGT_Pagination } from "../../../../../utils/utils";
import { getStoredRealm, getProviderGroupName } from "@/utils/auth-storage";

interface ProviderDetailsProps {
  formik: FormikProps<AddSuperBillForm>;
  viewBill: boolean;
  // A Patient Responsibility invoice bills the provider who rendered the
  // service on the posted claim, so that provider cannot be swapped here.
  isPatientResponsibilityInvoice?: boolean;
}

function ProviderDetails({
  formik,
  viewBill = true,
  isPatientResponsibilityInvoice = false,
}: ProviderDetailsProps) {
  const { values, setFieldValue, touched, errors } = formik;

  const isRenderingProviderLocked = !viewBill || isPatientResponsibilityInvoice;

  // Billing Provider = Practice Group (Billing Entity), no longer a searchable
  // individual provider. Mirror the Claim flow: show the Provider Group Name
  // read-only instead of a dropdown (ECRH-5370).
  // const [searchBillProviderGroupKey, setSearchBillProviderGroupKey] =
  //   useState<string>("");

  const billingProviderDisplayName =
    getProviderGroupName() ||
    Object.values(values.billingProviderId ?? {})[0] ||
    "";

  const [searchProviderKey, setSearchProviderKey] = useState<string>("");

  const [searchReferringProviderGroupKey, setSearchReferringProviderGroupKey] =
    useState<string>("");

  const [searchOrderingProviderGroupKey, setSearchOrderingProviderGroupKey] =
    useState<string>("");

  const xTENANTID = getStoredRealm() ?? "";

  const [renderingPagination, setRenderingPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [referringPagination, setReferringPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [orderingPagination, setOrderingPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  // A picked provider can live on a page that later scrolled out of
  // `content`, so keep every loaded provider by uuid to resolve the selection.
  const providerByUuid = useRef<Record<string, ProviderListItem>>({});

  const { data: providersList, isFetching: isFetchingProviders } =
    useProviderListV2({
      status: true,
      pagination: renderingPagination,
      searchString: searchProviderKey,
      xTENANTID: getStoredRealm() ?? "",
    });

  // Billing Provider is resolved from the Practice Group, not the individual
  // provider list.
  // const { data: providersBillList } = useProviderListV2({
  //   status: true,
  //   pagination:DEF_PAGINATION,
  //   searchString: searchBillProviderGroupKey,
  //   xTENANTID: xTENANTID,
  // });

  const { data: providersReferringList, isFetching: isFetchingReferring } =
    useProviderListV2({
      status: true,
      pagination: referringPagination,
      searchString: searchReferringProviderGroupKey,
      xTENANTID: xTENANTID,
    });

  const { data: providersOrderingList, isFetching: isFetchingOrdering } =
    useProviderListV2({
      status: true,
      pagination: orderingPagination,
      searchString: searchOrderingProviderGroupKey,
      xTENANTID: xTENANTID,
    });

  useEffect(() => {
    [
      ...(providersList?.data?.content ?? []),
      ...(providersReferringList?.data?.content ?? []),
      ...(providersOrderingList?.data?.content ?? []),
    ].forEach((it) => {
      if (it?.uuid) providerByUuid.current[it.uuid] = it;
    });
  }, [
    providersList?.data?.content,
    providersReferringList?.data?.content,
    providersOrderingList?.data?.content,
  ]);

  return (
    <Box id={"Provider Details"}>
      <CardView
        title="Provider Details"
        titleIcon={
          <ManageAccountsOutlinedIcon
            sx={{ color: "violet" }}
            fontSize="small"
          />
        }
      >
        <Grid item container spacing={2}>
          {/* Billing Provider = Practice Group (Billing Entity), read-only,
              populated with the Provider Group Name (same as the Claim flow). */}
          <Grid item xs={3}>
            <Input
              label="Billing Provider"
              textFieldProps={{
                name: "billingProviderId",
                placeholder: "Billing Provider",
                disabled: true,
                value: billingProviderDisplayName,
              }}
            />
          </Grid>
          {!viewBill && (
            <Grid item xs={3}>
              <Input
                label="Billing NPI Number"
                textFieldProps={{
                  placeholder: "Billing NPI Number",
                  disabled: true,
                  value: values?.billingProvideNPINumber,
                  onChange: (e) =>
                    formik.setFieldValue(
                      "billingProvideNPINumber",
                      e.target.value
                    ),
                }}
              />
            </Grid>
          )}
          <Grid item xs={3}>
            <CustomSelectScroll
              label="Referring Provider"
              readOnly={!viewBill}
              placeholder="Select Referring Provider"
              loading={isFetchingReferring}
              pagination={referringPagination}
              setPagination={setReferringPagination}
              totalElements={providersReferringList?.data?.page?.totalElements}
              options={providersReferringList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: it?.name,
              }))}
              onSearch={(_, val) => {
                setSearchReferringProviderGroupKey(val);
                setReferringPagination((p) => ({ ...p, page: 0 }));
              }}
              value={
                Object.keys(values.referringProviderId ?? {})?.[0] ?? undefined
              }
              onChange={(value: string | null) => {
                const curOption =
                  providerByUuid.current[value as string] ??
                  providersReferringList?.data?.content?.find(
                    (it) => it.uuid === value
                  );
                setFieldValue(
                  "referringProviderId",
                  curOption
                    ? {
                        [curOption.uuid]: curOption?.name,
                      }
                    : undefined
                );
              }}
            />
          </Grid>
          {!viewBill && (
            <Grid item xs={3}>
              <Input
                label="Referring NPI Number"
                textFieldProps={{
                  disabled: true,
                  placeholder: "Referring NPI Number",
                  value: values?.referringProviderNPINumber,
                  onChange: (e) =>
                    formik.setFieldValue(
                      "referringProviderNPINumber",
                      e.target.value
                    ),
                }}
              />
            </Grid>
          )}
          <Grid item xs={3}>
            <CustomSelectScroll
              label="Ordering Provider"
              readOnly={!viewBill}
              placeholder="Select Ordering Provider"
              loading={isFetchingOrdering}
              pagination={orderingPagination}
              setPagination={setOrderingPagination}
              totalElements={providersOrderingList?.data?.page?.totalElements}
              options={providersOrderingList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: it?.name,
              }))}
              onSearch={(_, val) => {
                setSearchOrderingProviderGroupKey(val);
                setOrderingPagination((p) => ({ ...p, page: 0 }));
              }}
              value={
                Object.keys(values.orderingProviderId ?? {})?.[0] ?? undefined
              }
              onChange={(value: string | null) => {
                const curOption =
                  providerByUuid.current[value as string] ??
                  providersOrderingList?.data?.content?.find(
                    (it) => it.uuid === value
                  );
                setFieldValue(
                  "orderingProviderId",
                  curOption
                    ? {
                        [curOption.uuid]: curOption?.name,
                      }
                    : undefined
                );
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <CustomSelectScroll
              readOnly={isRenderingProviderLocked}
              required
              name="renderingproviderId"
              label="Rendering Provider"
              placeholder="Select Rendering Provider"
              loading={isFetchingProviders}
              pagination={renderingPagination}
              setPagination={setRenderingPagination}
              totalElements={providersList?.data?.page?.totalElements}
              options={providersList?.data?.content?.map((it) => ({
                value: it?.uuid,
                label: it?.name,
              }))}
              onSearch={(_, val) => {
                setSearchProviderKey(val);
                setRenderingPagination((p) => ({ ...p, page: 0 }));
              }}
              value={
                Object.keys(values.renderingproviderId ?? {})?.[0] ?? undefined
              }
              onChange={(value: string | null) => {
                const curOption =
                  providerByUuid.current[value as string] ??
                  providersList?.data?.content?.find((it) => it.uuid === value);
                setFieldValue(
                  "renderingproviderId",
                  curOption
                    ? {
                        [curOption.uuid]: curOption?.name,
                      }
                    : undefined
                );
              }}
              error={touched?.renderingproviderId && errors.renderingproviderId}
              handleBlur={formik.handleBlur}
            />
          </Grid>
          {!viewBill && (
            <Grid item xs={3}>
              <Input
                label="Ordering NPI Number"
                textFieldProps={{
                  disabled: true,
                  placeholder: "Ordering NPI Number",
                  value: values?.orderingProviderNPINumber,
                  onChange: (e) =>
                    formik.setFieldValue(
                      "orderingProviderNPINumber",
                      e.target.value
                    ),
                }}
              />
            </Grid>
          )}
        </Grid>
      </CardView>
    </Box>
  );
}

export default ProviderDetails;
